/**
 * Health Monitor Page
 * System health checks and status
 */

import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import '../styles/validation.css';

const API_BASE = 'https://trading-api-1075463475276.us-central1.run.app/api/trading-validation';

const HealthMonitor: React.FC = () => {
  const [health, setHealth] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHealth = async () => {
      try {
        const res = await fetch(`${API_BASE}/health-checks`);
        const data = await res.json();
        setHealth(data.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching health:', error);
        setLoading(false);
      }
    };

    fetchHealth();
    const interval = setInterval(fetchHealth, 30000); // 30 sec
    return () => clearInterval(interval);
  }, []);

  if (loading) return <div className="validation-container"><div className="loading">Loading health status...</div></div>;

  const getStatusColor = (status: string) => {
    if (status === 'OK') return '#4CAF50';
    if (status === 'WARNING') return '#FF9800';
    return '#F44336';
  };

  return (
    <div className="validation-container">
      <div className="validation-header">
        <h1>System Health Monitor</h1>
        <p className="subtitle">Real-time health checks (updated every 30 seconds)</p>
      </div>

      {health && (
        <>
          {/* Overall Status */}
          <div className={`overall-status-card ${health.overall_status === 'HEALTHY' ? 'healthy' : 'warning'}`}>
            <h2>Overall Status: {health.overall_status}</h2>
            <p>All systems operational - Continue monitoring</p>
          </div>

          {/* Health Check Cards */}
          <div className="health-cards-grid">
            {/* API Check */}
            <div className="health-card">
              <div className="health-card-header">
                <h3>Alpaca API</h3>
                <div
                  className="status-indicator"
                  style={{ backgroundColor: getStatusColor(health.api?.status) }}
                ></div>
              </div>
              <div className="health-card-content">
                <div className="health-metric">
                  <span className="label">Status:</span>
                  <span className="value">{health.api?.status}</span>
                </div>
                <div className="health-metric">
                  <span className="label">Latency:</span>
                  <span className="value">{health.api?.latency_ms}ms</span>
                </div>
                <div className="health-metric">
                  <span className="label">Service:</span>
                  <span className="value">{health.api?.service}</span>
                </div>
              </div>
            </div>

            {/* Data Feed Check */}
            <div className="health-card">
              <div className="health-card-header">
                <h3>Data Feed</h3>
                <div
                  className="status-indicator"
                  style={{ backgroundColor: getStatusColor(health.data_feed?.status) }}
                ></div>
              </div>
              <div className="health-card-content">
                <div className="health-metric">
                  <span className="label">Status:</span>
                  <span className="value">{health.data_feed?.status}</span>
                </div>
                <div className="health-metric">
                  <span className="label">Lag:</span>
                  <span className="value">{health.data_feed?.lag_seconds}s</span>
                </div>
                <div className="health-metric">
                  <span className="label">Source:</span>
                  <span className="value">{health.data_feed?.source}</span>
                </div>
              </div>
            </div>

            {/* Database Check */}
            <div className="health-card">
              <div className="health-card-header">
                <h3>Database</h3>
                <div
                  className="status-indicator"
                  style={{ backgroundColor: getStatusColor(health.database?.status) }}
                ></div>
              </div>
              <div className="health-card-content">
                <div className="health-metric">
                  <span className="label">Status:</span>
                  <span className="value">{health.database?.status}</span>
                </div>
                <div className="health-metric">
                  <span className="label">Latency:</span>
                  <span className="value">{health.database?.latency_ms}ms</span>
                </div>
                <div className="health-metric">
                  <span className="label">Service:</span>
                  <span className="value">{health.database?.service}</span>
                </div>
              </div>
            </div>

            {/* Capital Check */}
            <div className="health-card">
              <div className="health-card-header">
                <h3>Capital Matching</h3>
                <div
                  className="status-indicator"
                  style={{ backgroundColor: getStatusColor(health.capital?.status) }}
                ></div>
              </div>
              <div className="health-card-content">
                <div className="health-metric">
                  <span className="label">Status:</span>
                  <span className="value">{health.capital?.status}</span>
                </div>
                <div className="health-metric">
                  <span className="label">Difference:</span>
                  <span className="value">{health.capital?.difference_pct?.toFixed(2)}%</span>
                </div>
                <div className="health-metric">
                  <span className="label">System:</span>
                  <span className="value">${health.capital?.system_capital}</span>
                </div>
                <div className="health-metric">
                  <span className="label">Broker:</span>
                  <span className="value">${health.capital?.broker_capital?.toFixed(0)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Health Metrics Summary */}
          <div className="widget">
            <h3>Health Summary</h3>
            <div className="health-summary">
              <div className="summary-item">
                <span className="label">All Checks Passing:</span>
                <span className={`value ${health.overall_status === 'HEALTHY' ? 'success' : 'warning'}`}>
                  {health.overall_status === 'HEALTHY' ? 'YES ✓' : 'NO ✗'}
                </span>
              </div>
              <div className="summary-item">
                <span className="label">Last Updated:</span>
                <span className="value">{new Date(health.timestamp).toLocaleTimeString()}</span>
              </div>
              <div className="summary-item">
                <span className="label">Recommended Action:</span>
                <span className="value">
                  {health.overall_status === 'HEALTHY' ? 'Continue monitoring' : 'Investigate issues'}
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default HealthMonitor;
